/**
 * Split `total` between `bucketCount` buckets as evenly as possible, the earlier ones taking the
 * remainder.
 *
 * @example getFrontLoadedCounts(8, 3) // [3, 3, 2]
 */
export function getFrontLoadedCounts(total: number, bucketCount: number): number[] {
  const base = Math.floor(total / bucketCount);
  const remainder = total % bucketCount;

  return Array.from({ length: bucketCount }, (_, index) => base + (index < remainder ? 1 : 0));
}

/**
 * The running right-hand edge of each width, which is how two divisions of the same run are lined
 * up against each other.
 *
 * @example getCumulativeEdges([30, 30, 30]) // [30, 60, 90]
 */
export function getCumulativeEdges(widths: number[]): number[] {
  const edges: number[] = [];
  let total = 0;

  for (const width of widths) {
    total += width;
    edges.push(total);
  }

  return edges;
}

/**
 * The widths a run of edges describes, measured from `startEdge`; the inverse of
 * getCumulativeEdges.
 *
 * @example getWidthsBetweenEdges([60, 90], 30) // [30, 30]
 */
export function getWidthsBetweenEdges(edges: number[], startEdge = 0): number[] {
  return edges.map((edge, index) => edge - (edges[index - 1] ?? startEdge));
}

/**
 * How many of `unionEdges` each of `edges` spans, given the combined edges of two divisions.
 * `startEdge` is where the measured run begins, for edges starting partway along rather than at 0.
 *
 * @example getSpansBetweenEdges([30, 60, 90], [20, 30, 40, 60, 80, 90]) // [2, 2, 2]
 * @example getSpansBetweenEdges([60, 90], [20, 30, 40, 60, 80, 90], 30) // [2, 2]
 */
export function getSpansBetweenEdges(
  edges: number[],
  unionEdges: number[],
  startEdge = 0
): number[] {
  // A start of zero is before the first edge, which is what indexOf reports for anything missing.
  return getWidthsBetweenEdges(
    edges.map(edge => unionEdges.indexOf(edge)),
    unionEdges.indexOf(startEdge)
  );
}
